import type { ComposeNode } from "@/lib/manufacturing/composePlan";
import type { MfgRecipeSummary } from "@/lib/manufacturing/types";

export const COMPOSE_MATCH_HELP = `Left = planned slots. Right = real catalog recipes. Click Use this to link. Already-linked recipes are hidden by default (one use per product is typical). Check the box below if the same assembly should appear in multiple slots. If suggestions miss, search the catalog.`;

export function matchSummary(
  node: ComposeNode,
  recipesById: ReadonlyMap<string, MfgRecipeSummary>,
): string {
  if (node.match.status === "matched") {
    const r = recipesById.get(node.match.recipe_id);
    return r ? `→ ${r.code}` : "→ linked";
  }
  if (node.match.status === "theoretical") return "will create new";
  return "needs match";
}

export function recipeSearchHaystack(r: MfgRecipeSummary): string {
  return `${r.code} ${r.name} ${r.tags.join(" ")}`.toLowerCase();
}

export function workingSkuLabel(
  selected: ComposeNode,
  products: readonly ComposeNode[],
): string | null {
  if (selected.role === "product") {
    return selected.name || selected.code;
  }
  const parents = products.filter((p) =>
    p.slots.some((s) => s.childNodeId === selected.id),
  );
  if (parents.length === 0) return null;
  return parents.map((p) => p.name || p.code).join(" · ");
}

export function componentListHaystack(n: ComposeNode): string {
  return `${n.code} ${n.name} ${n.slotKey ?? ""} ${n.variantKey ?? ""} ${n.sizeLabel ?? ""} ${n.styleKey ?? ""} ${n.tags.join(" ")}`.toLowerCase();
}

export function LinkStatusBadge({ status }: { status: ComposeNode["match"]["status"] }) {
  const cfg =
    status === "matched"
      ? { letter: "L", title: "Linked", className: "bg-emerald-500 text-white" }
      : status === "theoretical"
        ? { letter: "T", title: "Theoretical", className: "bg-amber-400 text-amber-950" }
        : { letter: "N", title: "Not linked yet", className: "bg-red-500 text-white" };
  return (
    <span
      title={cfg.title}
      aria-label={cfg.title}
      className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${cfg.className}`}
    >
      {cfg.letter}
    </span>
  );
}
